import { z } from "zod";

export const SITE_MESSENGER_CHANNELS = [
  "max",
  "telegram",
  "whatsapp",
  "vk",
  "instagram",
  "phone",
] as const;

export type SiteMessengerChannel = (typeof SITE_MESSENGER_CHANNELS)[number];

export type SiteMessengerButton = {
  enabled: boolean;
  url: string;
};

export type SiteMessengerButtons = Record<SiteMessengerChannel, SiteMessengerButton>;

export const SITE_THEME_IDS = [
  "current-pink",
  "masculine-graphite",
  "light-pearl",
  "lagoon-coral",
] as const;

export type SiteThemeId = (typeof SITE_THEME_IDS)[number];

export const DEFAULT_SITE_THEME_ID: SiteThemeId = "current-pink";

export const SiteThemeIdSchema = z.enum(SITE_THEME_IDS);

export const DEFAULT_PRE_VISIT_REMINDER_INTRO = "Запись уже скоро";
export const DEFAULT_PRE_VISIT_REMINDER_MESSAGE =
  "Ждём вас на записи. Если планы изменились, пожалуйста, перенесите или отмените визит заранее.";
export const DEFAULT_RETURN_REMINDER_MESSAGE =
  "Пора записаться снова. Выберите удобное время, когда будете готовы.";

export const BUSINESS_NICHES = [
  "MANICURE",
  "BROWS",
  "COSMETOLOGY",
  "HAIR",
  "MASSAGE",
  "OTHER",
] as const;

export type BusinessNiche = (typeof BUSINESS_NICHES)[number];

export const BusinessNicheSchema = z.enum(BUSINESS_NICHES);

export const BUSINESS_NICHE_LABELS: Record<BusinessNiche, string> = {
  MANICURE: "Маникюр / педикюр",
  BROWS: "Брови / ресницы",
  COSMETOLOGY: "Косметология",
  HAIR: "Волосы",
  MASSAGE: "Массаж",
  OTHER: "Другая ниша",
};

export const MARKETING_NICHE_SELECTIONS = [
  "MANICURE",
  "PEDICURE",
  "BROWS",
  "LASHES",
  "COSMETOLOGY",
  "MASSAGE",
  "HAIR",
  "BARBER",
  "DEPILATION",
  "SPA",
  "MAKEUP",
  "OTHER",
] as const;

export type MarketingNicheSelection = (typeof MARKETING_NICHE_SELECTIONS)[number];

export const MarketingNicheSelectionSchema = z.enum(MARKETING_NICHE_SELECTIONS);

export const MARKETING_NICHE_SELECTION_LABELS: Record<
  MarketingNicheSelection,
  string
> = {
  MANICURE: "Маникюр",
  PEDICURE: "Педикюр",
  BROWS: "Брови",
  LASHES: "Ресницы",
  COSMETOLOGY: "Косметология",
  MASSAGE: "Массаж",
  HAIR: "Волосы",
  BARBER: "Барбер",
  DEPILATION: "Депиляция / шугаринг",
  SPA: "SPA / уходы",
  MAKEUP: "Макияж",
  OTHER: "Другое",
};

export const PRIMARY_BUSINESS_NICHE_BY_MARKETING_SELECTION: Record<
  MarketingNicheSelection,
  BusinessNiche
> = {
  MANICURE: "MANICURE",
  PEDICURE: "MANICURE",
  BROWS: "BROWS",
  LASHES: "BROWS",
  COSMETOLOGY: "COSMETOLOGY",
  MASSAGE: "MASSAGE",
  HAIR: "HAIR",
  BARBER: "HAIR",
  DEPILATION: "COSMETOLOGY",
  SPA: "MASSAGE",
  MAKEUP: "OTHER",
  OTHER: "OTHER",
};

export const PAYMENT_BANKS = [
  "SBERBANK",
  "TBANK",
  "ALFABANK",
  "VTB",
  "GAZPROMBANK",
  "RAIFFEISENBANK",
  "ROSBANK",
  "SOVCOMBANK",
  "POCHTA_BANK",
  "MTS_BANK",
  "OZON_BANK",
  "YANDEX_BANK",
  "PSB",
  "RSKHB",
  "OTHER",
] as const;

export type PaymentBank = (typeof PAYMENT_BANKS)[number];

export const PaymentBankSchema = z.enum(PAYMENT_BANKS);

export const DIRECT_SBP_PAYMENT_BANKS = ["SBERBANK", "TBANK"] as const;

export type DirectSbpPaymentBank = (typeof DIRECT_SBP_PAYMENT_BANKS)[number];

export const PAYMENT_BANK_LABELS: Record<PaymentBank, string> = {
  SBERBANK: "Сбербанк",
  TBANK: "Т-Банк",
  ALFABANK: "Альфа-Банк",
  VTB: "ВТБ",
  GAZPROMBANK: "Газпромбанк",
  RAIFFEISENBANK: "Райффайзенбанк",
  ROSBANK: "Росбанк",
  SOVCOMBANK: "Совкомбанк",
  POCHTA_BANK: "Почта Банк",
  MTS_BANK: "МТС Банк",
  OZON_BANK: "Ozon Банк",
  YANDEX_BANK: "Яндекс Банк",
  PSB: "Промсвязьбанк",
  RSKHB: "Россельхозбанк",
  OTHER: "Другой",
};

export function normalizePaymentBanks(input: unknown, legacyBank?: unknown): PaymentBank[] {
  const rawBanks = Array.isArray(input) ? input : [];
  const banks: PaymentBank[] = [];

  for (const value of rawBanks) {
    if (typeof value !== "string") continue;
    if (!(PAYMENT_BANKS as readonly string[]).includes(value)) continue;
    if (!banks.includes(value as PaymentBank)) {
      banks.push(value as PaymentBank);
    }
  }

  if (banks.length > 0) return banks;

  if (typeof legacyBank === "string" && (PAYMENT_BANKS as readonly string[]).includes(legacyBank)) {
    return [legacyBank as PaymentBank];
  }

  return [];
}

export function formatPaymentBankList(banks: readonly PaymentBank[], otherBankName?: string | null): string {
  return banks
    .map((bank) => {
      if (bank !== "OTHER") return PAYMENT_BANK_LABELS[bank];
      const customName = otherBankName?.trim();
      return customName || "Другой банк";
    })
    .join(", ");
}

export function getFirstDirectSbpPaymentBank(banks: readonly PaymentBank[]): DirectSbpPaymentBank | null {
  const directBank = banks.find((bank): bank is DirectSbpPaymentBank =>
    (DIRECT_SBP_PAYMENT_BANKS as readonly string[]).includes(bank),
  );

  return directBank ?? null;
}

export function normalizeSiteThemeId(input: unknown): SiteThemeId {
  return typeof input === "string" && (SITE_THEME_IDS as readonly string[]).includes(input)
    ? (input as SiteThemeId)
    : DEFAULT_SITE_THEME_ID;
}

export const DEFAULT_SITE_MESSENGER_BUTTONS: SiteMessengerButtons = {
  max: { enabled: false, url: "" },
  telegram: { enabled: false, url: "" },
  whatsapp: { enabled: false, url: "" },
  vk: { enabled: false, url: "" },
  instagram: { enabled: false, url: "" },
  phone: { enabled: false, url: "" },
};

export const DEFAULT_ENABLED_SITE_MESSENGER_BUTTONS: SiteMessengerButtons = {
  max: { enabled: true, url: "" },
  telegram: { enabled: true, url: "" },
  whatsapp: { enabled: true, url: "" },
  vk: { enabled: true, url: "" },
  instagram: { enabled: true, url: "" },
  phone: { enabled: true, url: "" },
};

function isHttpUrl(value: string): boolean {
  return /^https?:\/\/[^\s]+$/i.test(value);
}

function isPhoneValue(value: string): boolean {
  const digits = value.replace(/\D/g, "");
  return digits.length >= 5 && digits.length <= 15;
}

function normalizeContactValue(channel: SiteMessengerChannel, value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";

  if (channel === "phone") {
    return isPhoneValue(trimmed) ? trimmed : "";
  }

  return isHttpUrl(trimmed) ? trimmed : "";
}

const HttpMessengerButtonPatchSchema = z
  .object({
    enabled: z.boolean(),
    url: z.string().trim().max(500),
  })
  .superRefine((value, ctx) => {
    if (value.url && !isHttpUrl(value.url)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["url"],
        message: "Messenger URL must be an http(s) URL",
      });
    }
  });

const PhoneMessengerButtonPatchSchema = z
  .object({
    enabled: z.boolean(),
    url: z.string().trim().max(50),
  })
  .superRefine((value, ctx) => {
    if (value.url && !isPhoneValue(value.url)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["url"],
        message: "Call phone must include a valid phone number",
      });
    }
  });

export const SiteMessengerButtonsSchema = z
  .object({
    max: HttpMessengerButtonPatchSchema.optional(),
    telegram: HttpMessengerButtonPatchSchema.optional(),
    whatsapp: HttpMessengerButtonPatchSchema.optional(),
    vk: HttpMessengerButtonPatchSchema.optional(),
    instagram: HttpMessengerButtonPatchSchema.optional(),
    phone: PhoneMessengerButtonPatchSchema.optional(),
  })
  .strict();

const LenientSiteMessengerButtonsSchema = z
  .object({
    max: z.object({ enabled: z.boolean().optional(), url: z.string().optional() }).optional(),
    telegram: z.object({ enabled: z.boolean().optional(), url: z.string().optional() }).optional(),
    whatsapp: z.object({ enabled: z.boolean().optional(), url: z.string().optional() }).optional(),
    vk: z.object({ enabled: z.boolean().optional(), url: z.string().optional() }).optional(),
    instagram: z.object({ enabled: z.boolean().optional(), url: z.string().optional() }).optional(),
    phone: z.object({ enabled: z.boolean().optional(), url: z.string().optional() }).optional(),
  })
  .passthrough();

export function normalizeSiteMessengerButtons(input: unknown): SiteMessengerButtons {
  const parsed = LenientSiteMessengerButtonsSchema.safeParse(input);
  const raw = parsed.success ? parsed.data : {};
  const normalized = Object.fromEntries(
    SITE_MESSENGER_CHANNELS.map((channel) => [
      channel,
      { ...DEFAULT_SITE_MESSENGER_BUTTONS[channel] },
    ]),
  ) as SiteMessengerButtons;

  for (const channel of SITE_MESSENGER_CHANNELS) {
    const button = raw[channel];
    if (!button) continue;

    const url = typeof button.url === "string" ? button.url.trim() : "";
    normalized[channel] = {
      enabled: Boolean(button.enabled),
      url: normalizeContactValue(channel, url),
    };
  }

  return normalized;
}

export function buildDefaultEnabledSiteMessengerButtons(phone?: string | null): SiteMessengerButtons {
  const buttons = Object.fromEntries(
    SITE_MESSENGER_CHANNELS.map((channel) => [
      channel,
      { ...DEFAULT_ENABLED_SITE_MESSENGER_BUTTONS[channel] },
    ]),
  ) as SiteMessengerButtons;

  buttons.phone = {
    enabled: true,
    url: phone ? normalizeContactValue("phone", phone) : "",
  };

  return buttons;
}

export function mergeSiteMessengerButtons(
  current: unknown,
  patch: z.infer<typeof SiteMessengerButtonsSchema>,
): SiteMessengerButtons {
  const normalized = normalizeSiteMessengerButtons(current);

  for (const channel of SITE_MESSENGER_CHANNELS) {
    const value = patch[channel];
    if (!value) continue;
    normalized[channel] = {
      enabled: value.enabled,
      url: value.url.trim(),
    };
  }

  return normalized;
}

export const BusinessSettingsSchema = z.object({
  welcomeMessage: z.string().optional(),
  bookingConfirmMessage: z.string().optional(),
  cancellationMessage: z.string().optional(),
  reminderMessage: z.string().optional(),
  returnReminderMessage: z.string().optional(),
  reviewRequestMessage: z.string().optional(),
});

export type BusinessSettings = z.infer<typeof BusinessSettingsSchema>;

export const CreateBusinessSchema = z.object({
  slug: z
    .string()
    .min(3)
    .max(50)
    .regex(/^[a-z0-9-]+$/),
  name: z.string().min(1).max(100),
  phone: z.string().optional(),
  brandColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  city: z.string().optional(),
  address: z.string().optional(),
});

export type CreateBusiness = z.infer<typeof CreateBusinessSchema>;

export const UpdateBusinessSchema = CreateBusinessSchema.partial()
  .omit({ slug: true })
  .extend({
    tagline: z.string().max(80).optional(),
    description: z.string().max(1000).optional(),
    logoUrl: z.string().url().optional(),
    templateId: z.enum(["classic"]).optional(),
    niche: BusinessNicheSchema.optional(),
    reviewLinkUrl: z.string().url().optional(),
    reviewMessage: z.string().max(500).optional(),
    reminder2hMessage: z.string().max(500).optional(),
    returnReminderMessage: z.string().max(500).optional(),
    returnReminderDays: z.number().int().min(1).max(365).optional(),
    reminder2hMinutesBefore: z.number().int().min(30).max(1440).optional(),
    bookingWindowDays: z.number().int().min(1).max(365).optional(),
    featureReminder2h: z.boolean().optional(),
    featureReturnReminder: z.boolean().optional(),
    featureReviewRequest: z.boolean().optional(),
    paymentPhone: z.string().optional(),
    paymentBank: z.enum(["SBERBANK", "TBANK"]).optional(),
    paymentBanks: z.array(PaymentBankSchema).max(PAYMENT_BANKS.length).optional(),
    paymentOtherBankName: z.string().trim().max(80).optional(),
    masterNotificationChannel: z.enum(["TELEGRAM", "MAX"]).optional(),
    siteThemeId: SiteThemeIdSchema.optional(),
    siteMessengerButtons: SiteMessengerButtonsSchema.optional(),
    citySelectionToken: z.string().min(20).max(3000).optional(),
    addressSelectionToken: z.string().min(20).max(3000).optional(),
    noAddress: z.boolean().optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const hasLocationPatch =
      "city" in value ||
      "address" in value ||
      "citySelectionToken" in value ||
      "addressSelectionToken" in value ||
      "noAddress" in value;

    if (!hasLocationPatch) return;

    if (!value.city || !value.citySelectionToken) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["citySelectionToken"],
        message: "Select city from suggestions",
      });
    }

    if (value.noAddress) {
      if (value.address || value.addressSelectionToken) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["address"],
          message: "Address fields must be empty when noAddress is selected",
        });
      }
      return;
    }

    if (!value.address || !value.addressSelectionToken) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["addressSelectionToken"],
        message: "Select address from suggestions",
      });
    }
  });
export type UpdateBusiness = z.infer<typeof UpdateBusinessSchema>;
